import { useState } from 'react';
import moment from 'moment';

// Utility function to calculate mortgage payment
const calculateMortgagePayment = (principal, annualRate, years) => {
  const monthlyRate = (annualRate / 100) / 12;
  const numberOfPayments = years * 12;
  return principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments) 
         / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
};

export const useCalculation = () => {
  const [results, setResults] = useState([]);
  const [finalMonth, setFinalMonth] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [inputsChanged, setInputsChanged] = useState(false);
  const [pageSize, setPageSize] = useState(12);
  const [lastCalculatedValues, setLastCalculatedValues] = useState({
    startDate: moment(),
  });
  const [cumulativeTotals, setCumulativeTotals] = useState({
    totalContributions: 0,
    totalGrowth: 0,
    totalFees: 0,
    totalInterestCharged: 0,
    totalAdditionalSpending: 0,
    totalPaymentsMade: 0,
    totalMortgageInterest: 0,
    totalMortgagePayments: 0,
    totalEquityBuilt: 0,
    totalOverpayments: 0
  });

  const validateInputs = (state) => {
    const { 
      calculationType, 
      balance, 
      interestRate, 
      monthlyContribution,
      // Mortgage-specific inputs
      propertyValue,
      mortgageAmount,
      deposit,
      fixedRate,
      fixedTerm,
      totalTerm,
      variableRate,
      mortgageStartMonth,
      mortgageStartYear
    } = state;

    if (calculationType === 'mortgage') {
      // Property value or mortgage amount must be provided
      if (!propertyValue && !mortgageAmount) {
        return 'Either Property Value or Mortgage Amount is required';
      }
    
      // Determine property value and deposit
      const propValue = propertyValue || (mortgageAmount + (deposit || 0));
      const calculatedDeposit = deposit || (propValue - mortgageAmount);
    
      // Validate other mortgage inputs
      if (!fixedRate || !fixedTerm || !totalTerm || !variableRate) {
        return 'All mortgage term fields are required';
      }
    
      // Validate deposit/property value relationship
      if (calculatedDeposit >= propValue) {
        return 'Deposit cannot be greater than or equal to property value';
      }
    
      // Validate fixed term
      if (parseFloat(fixedTerm) > parseFloat(totalTerm)) {
        return 'Fixed rate term cannot be longer than total mortgage term';
      }
    }

    if (calculationType === 'debt') {
      if (!balance || !interestRate || (!monthlyContribution && !state.targetMonths)) {
        return 'Please enter required debt values';
      }
      const monthlyInterestRate = (interestRate / 100) / 12;
      const monthlyInterest = balance * monthlyInterestRate;
      const spending = parseFloat(state.additionalSpending) || 0;
      
      if (monthlyContribution && monthlyContribution <= spending + monthlyInterest) {
        return `Monthly payment must be greater than ${(spending + monthlyInterest).toFixed(2)} (additional spending plus monthly interest)`;
      }
    }

    return '';
  };

  const currencyFormatter = (value, currency) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    let formatted = formatter.format(value);
    const currencySymbols = { 'USD': '$', 'GBP': '£', 'EUR': '€' };
    const symbol = currencySymbols[currency] || currency;
    formatted = formatted.replace(/[A-Z]{3}\s?/, symbol);

    return formatted;
  };

  const calculateMortgageAmortizationSchedule = (options) => {
    const {
      propertyValue,
      mortgageAmount,
      deposit,
      fixedRate,
      fixedTerm,
      totalTerm,
      variableRate,
      mortgageType,
      mortgageStartMonth,
      mortgageStartYear,
      monthlyOverpayment = 0,
      lumpSumOverpayments = [],
      overpaymentStrategy = 'reduce-term'
    } = options;
  
    // Determine loan amount and property value
    const propValue = propertyValue || mortgageAmount + (deposit || 0);
    const calculatedDeposit = deposit || propValue - mortgageAmount;
    const loanAmount = mortgageAmount || (propValue - calculatedDeposit);
  
    const fixedMonthlyPayment = calculateMortgagePayment(loanAmount, fixedRate, totalTerm);
    const variableMonthlyPayment = calculateMortgagePayment(loanAmount, variableRate, totalTerm - fixedTerm);
    
    let currentBalance = loanAmount;
    let month = 0;
    const schedule = [];
    let totalInterestPaid = 0;
    let totalPayments = 0;
    let totalPrincipalPaid = 0;
    let totalOverpayments = 0;
    let cumulativeInterest = 0;
  
    // Ensure valid start date - using the provided mortgage start date
    const startDate = moment([
      mortgageStartYear || moment().year(), 
      mortgageStartMonth || moment().month()
    ]);
  
    // Calculate original total interest (without overpayments) for comparison
    let tempBalance = loanAmount;
    let originalTotalInterest = 0;
    
    for (let m = 0; m < totalTerm * 12; m++) {
      const isFixed = m < fixedTerm * 12;
      const rate = isFixed ? fixedRate : variableRate;
      const monthlyRate = (rate / 100) / 12;
      const interest = tempBalance * monthlyRate;
      originalTotalInterest += interest;
      if (mortgageType === 'repayment') {
        const payment = isFixed ? fixedMonthlyPayment : variableMonthlyPayment;
        tempBalance -= (payment - interest);
      }
    }
  
    // Calculate with overpayments
    while (month < totalTerm * 12 && currentBalance > 0) {
      const currentDate = startDate.clone().add(month, 'months');
      const isFixedPeriod = month < fixedTerm * 12;
      const currentRate = isFixedPeriod ? fixedRate : variableRate;
      const baseMonthlyPayment = isFixedPeriod ? fixedMonthlyPayment : variableMonthlyPayment;
      const monthlyRate = (currentRate / 100) / 12;
      
      // Calculate interest
      const interestPayment = currentBalance * monthlyRate;
  
      // Find any lump sum overpayment for this month
      const lumpSumOverpayment = Array.isArray(lumpSumOverpayments) ? 
        lumpSumOverpayments.find(
          op => moment(op.date).month() === currentDate.month() &&
                moment(op.date).year() === currentDate.year()
        )?.amount || 0 : 0;
  
      // Calculate total overpayment for this month
      const totalMonthlyOverpayment = parseFloat(monthlyOverpayment || 0) + parseFloat(lumpSumOverpayment || 0);
      
      let principalPayment = 0;
      if (mortgageType === 'repayment') {
        principalPayment = baseMonthlyPayment - interestPayment + totalMonthlyOverpayment;
      } else { // interest-only
        principalPayment = totalMonthlyOverpayment;
        if (month === totalTerm * 12 - 1) {
          principalPayment += currentBalance;
        }
      }
  
      // Ensure we don't overpay more than remaining balance
      principalPayment = Math.min(principalPayment, currentBalance);
      currentBalance = Math.max(0, currentBalance - principalPayment);
      
      cumulativeInterest += interestPayment;
      totalInterestPaid += interestPayment;
      totalPayments += baseMonthlyPayment + totalMonthlyOverpayment;
      totalPrincipalPaid += principalPayment;
      totalOverpayments += totalMonthlyOverpayment;
  
      schedule.push({
        monthDate: currentDate,
        startBalance: currentBalance + principalPayment,
        payment: baseMonthlyPayment,
        overpayment: totalMonthlyOverpayment,
        principalPayment,
        interestPayment,
        cumulativeInterest,
        endBalance: currentBalance,
        rate: currentRate,
        isFixedRate: isFixedPeriod
      });
  
      month++;
      
      // Break early if balance is cleared
      if (currentBalance <= 0) break;
    }
  
    // Calculate the impact of overpayments
    const termReduction = (totalTerm * 12) - month;
    const interestSaved = originalTotalInterest - totalInterestPaid;
  
    return {
      schedule,
      summary: {
        loanAmount,
        propertyValue: propValue,
        deposit: calculatedDeposit,
        totalInterestPaid,
        totalPayments,
        totalPrincipalPaid,
        monthlyPaymentFixed: fixedMonthlyPayment,
        monthlyPaymentVariable: variableMonthlyPayment,
        cumulativeInterest,
        totalOverpayments,
        originalTerm: totalTerm * 12,
        newTerm: month,
        termReduction,
        interestSaved
      }
    };
  }

  const calculatePayments = (state) => {
    setInputsChanged(false);
    setErrorMessage('');
  
    const validationError = validateInputs(state);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
  
    const startDateMoment = moment([
      state.mortgageStartYear || moment().year(), 
      state.mortgageStartMonth || moment().month()
    ]);
  
    let tableData = [];
    let totalContributions = 0;
    let totalGrowth = 0;
    let totalFees = 0;
    let totalInterestCharged = 0;
    let totalAdditionalSpending = 0;
    let totalPaymentsMade = 0;
    let totalMortgageInterest = 0;
    let totalMortgagePayments = 0;
    let totalEquityBuilt = 0;
    let totalOverpayments = 0;

    let newLastCalculatedValues = { ...state, startDate: startDateMoment };
  
    if (state.calculationType === 'mortgage') {
      // Ensure overpayment values are passed to the mortgage calculator
      const mortgageResults = calculateMortgageAmortizationSchedule({
        ...state,
        // Make sure to include these values
        monthlyOverpayment: state.monthlyOverpayment || 0,
        lumpSumOverpayments: state.lumpSumOverpayments || [],
        overpaymentStrategy: state.overpaymentStrategy || 'reduce-term'
      });
      
      const { schedule, summary } = mortgageResults;
    
      tableData = schedule;
      totalMortgageInterest = summary.totalInterestPaid;
      totalMortgagePayments = summary.totalPayments;
      totalEquityBuilt = summary.totalPrincipalPaid;
      totalOverpayments = summary.totalOverpayments || 0;
    
      newLastCalculatedValues = {
        ...state,
        mortgageAmount: summary.loanAmount,
        propertyValue: summary.propertyValue,
        deposit: summary.deposit,
        monthlyPaymentFixed: summary.monthlyPaymentFixed,
        monthlyPaymentVariable: summary.monthlyPaymentVariable,
        calculatedContribution: schedule[0]?.payment,
        finalBalance: schedule[schedule.length - 1]?.endBalance || 0,
        startDate: startDateMoment,
        originalTerm: summary.originalTerm,
        newTerm: summary.newTerm,
        termReduction: summary.termReduction,
        interestSaved: summary.interestSaved
      };
  
      setLastCalculatedValues(newLastCalculatedValues);
  
    } else if (state.calculationType === 'debt') {
      // Existing debt calculations (previous implementation)
      const annualRateDecimal = (parseFloat(state.interestRate) || 0) / 100;
      const monthlyInterestRate = annualRateDecimal / 12;
      let calculatedContribution = parseFloat(state.monthlyContribution);
      const additionalMonthlySpending = parseFloat(state.additionalSpending) || 0;
      let currentBalance = parseFloat(state.balance) || 0;
      let month = 0;
  
      if (state.targetMonths) {
        const n = parseInt(state.targetMonths);
        const r = monthlyInterestRate;
        const P = currentBalance;
        calculatedContribution = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) + additionalMonthlySpending;
      }
  
      const targetMonthsNum = parseInt(state.targetMonths) || 720;
  
      while (month < targetMonthsNum && currentBalance > 0) {
        const interestAmount = currentBalance * monthlyInterestRate;
        const totalDue = currentBalance + interestAmount + additionalMonthlySpending;
        const actualPayment = Math.min(calculatedContribution, totalDue);
        const newBalance = Math.max(0, totalDue - actualPayment);
  
        tableData.push({
          key: month,
          monthDate: startDateMoment.clone().add(month, 'months'),
          startBalance: parseFloat(currentBalance),
          interestAmount: parseFloat(interestAmount),
          additionalSpending: parseFloat(additionalMonthlySpending),
          contribution: parseFloat(actualPayment),
          endBalance: parseFloat(newBalance)
        });
  
        totalInterestCharged += interestAmount;
        totalAdditionalSpending += additionalMonthlySpending;
        totalPaymentsMade += actualPayment;
  
        currentBalance = newBalance;
        if (currentBalance <= 0) break;
        month++;
      }

      newLastCalculatedValues = {
        ...state,
        calculatedContribution: calculatedContribution,
        finalBalance: tableData[tableData.length - 1]?.endBalance || 0,
        startDate: startDateMoment
      };
  
    } else if (state.calculationType === 'investment') {
      // Existing investment calculations (previous implementation)
      const totalMonths = (parseInt(state.years) || 0) * 12 + (parseInt(state.months) || 0);
      if (totalMonths === 0) {
        setErrorMessage('Please enter a valid investment duration');
        return;
      }
  
      const maxContributionMonths = parseInt(state.contributionMonths) || totalMonths;
      let currentBalance = parseFloat(state.balance) || 0;
      const monthlyInterestRate = (parseFloat(state.interestRate) / 100) / 12;
      const monthlyFeeRate = (parseFloat(state.annualFeeRate) || 0) / 100 / 12;
      let calculatedContribution = parseFloat(state.monthlyContribution);
  
      if (state.targetEndBalance) {
        // Calculate required contribution for target balance
        const r = monthlyInterestRate;
        const f = monthlyFeeRate;
        const P = currentBalance;
        const A = parseFloat(state.targetEndBalance);
        const n = totalMonths;
        const c = parseInt(state.contributionMonths) || n;
  
        if (r === 0) {
          calculatedContribution = (A - P) / c + (A * f);
        } else {
          const growthAfterContributions = Math.pow(1 + r - f, n - c);
          const contributionGrowth = (Math.pow(1 + r - f, c) - 1) / (r - f);
          calculatedContribution = (A - P * Math.pow(1 + r - f, n)) / 
                                 (contributionGrowth * growthAfterContributions);
        }
      }
  
      for (let month = 0; month < totalMonths; month++) {
        const monthlyContribution = month < maxContributionMonths ? calculatedContribution : 0;
        const balanceAfterGrowth = currentBalance * (1 + monthlyInterestRate);
        const feeAmount = balanceAfterGrowth * monthlyFeeRate;
        const balanceAfterFee = balanceAfterGrowth - feeAmount;
        const newBalance = balanceAfterFee + monthlyContribution;
  
        tableData.push({
          key: month,
          monthDate: startDateMoment.clone().add(month, 'months'),
          startBalance: currentBalance,
          interestAmount: balanceAfterGrowth - currentBalance,
          feeAmount,
          contribution: monthlyContribution,
          endBalance: newBalance
        });
  
        totalGrowth += balanceAfterGrowth - currentBalance;
        totalFees += feeAmount;
        totalContributions += monthlyContribution;
  
        currentBalance = newBalance;
      }
      
      newLastCalculatedValues = {
        ...state,
        calculatedContribution: calculatedContribution,
        finalBalance: tableData[tableData.length - 1]?.endBalance || 0,
        startDate: startDateMoment
      };
    }
  
    setResults(tableData);
  
    if (tableData.length > 0) {
      setFinalMonth(tableData[tableData.length - 1].monthDate);
    }
  
    setCumulativeTotals({
      totalContributions,
      totalGrowth,
      totalFees,
      totalInterestCharged,
      totalAdditionalSpending,
      totalPaymentsMade,
      totalMortgageInterest,
      totalMortgagePayments,
      totalEquityBuilt,
      totalOverpayments
    });
  
    setLastCalculatedValues(newLastCalculatedValues);
    setInputsChanged(false);
  };

  const resetCalculation = () => {
    setResults([]);
    setFinalMonth(null);
    setErrorMessage('');
    setLastCalculatedValues({
      startDate: moment(),
    });
    setCumulativeTotals({
      totalContributions: 0,
      totalGrowth: 0,
      totalFees: 0,
      totalInterestCharged: 0,
      totalAdditionalSpending: 0,
      totalPaymentsMade: 0,
      totalMortgageInterest: 0,
      totalMortgagePayments: 0,
      totalEquityBuilt: 0,
      totalOverpayments: 0
    });
  };
  
  const getTableColumns = (calculationType, currency) => {
    const baseColumns = [
      {
        title: 'Month',
        dataIndex: 'monthDate',
        key: 'month',
        render: (date) => date.format('MMMM YYYY'),
      },
      {
        title: 'Balance at Start',
        dataIndex: 'startBalance',
        key: 'startBalance',
        render: (value) => currencyFormatter(value, currency),
      }
    ];

    if (calculationType === 'mortgage') {
      return [
        ...baseColumns,
        {
          title: 'Monthly Payment',
          dataIndex: 'payment',
          key: 'payment',
          render: (value) => currencyFormatter(value, currency),
        },
        {
          title: 'Overpayment',
          dataIndex: 'overpayment',
          key: 'overpayment',
          render: (value) => currencyFormatter(value || 0, currency),
        },
        {
          title: 'Principal',
          dataIndex: 'principalPayment',
          key: 'principalPayment',
          render: (value) => currencyFormatter(value, currency),
        },
        {
          title: 'Interest',
          dataIndex: 'interestPayment',
          key: 'interestPayment',
          render: (value) => currencyFormatter(value, currency),
        },
        {
          title: 'Balance at End',
          dataIndex: 'endBalance',
          key: 'endBalance',
          render: (value) => currencyFormatter(value, currency),
        },
        {
          title: 'Interest Rate',
          dataIndex: 'rate',
          key: 'rate',
          render: (value, record) => `${value}% ${record.isFixedRate ? '(Fixed)' : '(Variable)'}`,
        }
      ];
    }

    return [
      ...baseColumns,
      {
        title: calculationType === 'debt' ? 'Interest Charged' : 'Growth Amount',
        dataIndex: 'interestAmount',
        key: 'interestAmount',
        render: (value) => currencyFormatter(value, currency),
      },
      {
        title: calculationType === 'debt' ? 'Additional Spending' : 'Fee Amount',
        dataIndex: calculationType === 'debt' ? 'additionalSpending' : 'feeAmount',
        key: calculationType === 'debt' ? 'additionalSpending' : 'feeAmount',
        render: (value) => currencyFormatter(value, currency),
      },
      {
        title: calculationType === 'debt' ? 'Payment' : 'Contribution',
        dataIndex: 'contribution',
        key: 'contribution',
        render: (value) => currencyFormatter(value, currency),
      },
      {
        title: 'Balance at End',
        dataIndex: 'endBalance',
        key: 'endBalance',
        render: (value) => currencyFormatter(value, currency),
      },
    ];
  };

  const validateDebtInputs = (balance, interestRate, monthlyPayment, additionalSpending) => {
    const currentBalance = parseFloat(balance) || 0;
    const annualRate = parseFloat(interestRate) || 0;
    const payment = parseFloat(monthlyPayment) || 0;
    const spending = parseFloat(additionalSpending) || 0;
  
    const monthlyInterestRate = (annualRate / 100) / 12;
    const monthlyInterest = currentBalance * monthlyInterestRate;
  
    if (payment <= spending + monthlyInterest) {
      return `Monthly payment must be greater than ${(spending + monthlyInterest).toFixed(2)} (additional spending plus monthly interest)`;
    }
    return '';
  };

  return {
    results,
    finalMonth,
    errorMessage,
    setErrorMessage,
    inputsChanged,
    pageSize,
    cumulativeTotals,
    calculatePayments,
    validateDebtInputs,
    currencyFormatter,
    getTableColumns,
    setPageSize,
    setInputsChanged,
    lastCalculatedValues,
    resetCalculation,
    validateInputs
  };
};

export default useCalculation;